import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import type { HeroBlockAction } from "../../../../cms/components/blocks/HeroBlock/HeroBlock.props";
import type { IHeroBackgroundImageProps } from "./HeroBackgroundImage.props";
import { RichTextHtml } from "../../../../cms/components/RichTextHtml";

const Image = resolveKaajPrimitive("Image");
const Link = resolveKaajPrimitive("Link");

const isExternalBlank = (target?: string) => target === "_blank";

const HeroActions = ({ buttons }: { buttons?: HeroBlockAction[] | null }) => {
  const visibleActions = buttons ?? [];

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-3 max-[420px]:w-full">
      {visibleActions.map((action, index) => {
        const label = action.text;
        const style = action.style;
        const isPrimary = style === "primary" || (index === 0 && style !== "link");

        return (
          <Link
            key={`${label}-${action.href}`}
            href={action.href}
            target={action.target}
            rel={isExternalBlank(action.target) ? "noopener noreferrer" : undefined}
            aria-label={action.ariaLabel || label}
            className={cn(
              "inline-flex min-h-11 items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition-[background-color,border-color,box-shadow,transform] duration-200 motion-reduce:transition-none max-[420px]:w-full",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2144FF]",
              isPrimary
                ? "bg-[#2144FF] text-white shadow-[0_14px_34px_rgba(33,68,255,0.25)] hover:bg-[#132EDB] active:bg-[#0F25B8] motion-safe:hover:-translate-y-0.5"
                : "border border-[#D9E1FF] bg-white text-[#2144FF] hover:border-[#2144FF] hover:bg-[#F6F1FF] hover:shadow-[0_12px_30px_rgba(33,68,255,0.12)] active:border-[#132EDB] active:text-[#132EDB]",
            )}
          >
            {label}
            {isPrimary ? (
              <span aria-hidden="true" className="ml-2">
                →
              </span>
            ) : null}
          </Link>
        );
      })}
    </div>
  );
};

const HeroCopy = ({ props }: { props: IHeroBackgroundImageProps }) => {
  return (
    <div className="flex w-full min-w-0 flex-col gap-5 sm:gap-6">
      {props.eyebrow || props.heading ? (
        <div>
          {props.eyebrow ? (
            <p className="mb-4 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2144FF]">
              {props.eyebrow}
            </p>
          ) : null}
          {props.heading ? (
            <h1 className="text-balance text-[2.25rem] lg:text-[3.95rem] font-extrabold leading-[1.04] tracking-[-0.04em] text-[#151927]">
              {props.heading}
            </h1>
          ) : null}
        </div>
      ) : null}
      {props.subheading ? (
        <RichTextHtml
          className="text-pretty text-base leading-7 text-[#4B5568] sm:text-lg sm:leading-8"
          html={props.subheading}
        />
      ) : null}
      <HeroActions buttons={props.buttons} />
    </div>
  );
};

export const HeroBackgroundImage = (props: IHeroBackgroundImageProps) => {
  const backdropImage = props.image ?? undefined;
  const backdropSrc = backdropImage?.defaultSrc;
  const hasBackdropImage = Boolean(backdropSrc);

  return (
    <div className="relative isolate w-full md:flex md:h-full md:flex-col md:justify-center">
      <div
        aria-hidden="true"
        className={cn(
          "relative h-[24rem] w-full overflow-hidden sm:h-[30rem] md:pointer-events-none md:absolute md:inset-y-0 md:left-1/2 md:-z-10 md:h-auto md:max-w-[150rem] md:-translate-x-1/2",
          hasBackdropImage
            ? "bg-[#080B1A] shadow-[0_28px_80px_rgba(33,68,255,0.16)]"
            : "bg-[#FAFBFF]",
        )}
      >
        {backdropSrc ? (
          <Image
            src={backdropSrc}
            alt=""
            width={backdropImage?.width ?? undefined}
            height={backdropImage?.height ?? undefined}
            sizes="(min-width: 2400px) 2400px, 100vw"
            className="absolute inset-0 h-full w-full scale-[1.03] object-cover object-center opacity-90 brightness-[0.78] contrast-[0.72] saturate-[0.9]"
            loading={props.priority ? "eager" : "lazy"}
            priority={props.priority ?? undefined}
            srcSet={backdropImage?.srcSet ?? undefined}
            style={
              backdropImage?.focalPoint
                ? {
                    objectPosition: `${backdropImage.focalPoint.x}% ${backdropImage.focalPoint.y}%`,
                  }
                : undefined
            }
          />
        ) : null}
        <div
          aria-hidden="true"
          className={cn(
            "absolute inset-0 ring-1",
            hasBackdropImage ? "ring-white/22" : "ring-[#D9E1FF]",
          )}
        />
      </div>

      <div className="kaaj-container relative z-10 py-8 sm:py-10 md:py-0">
        <div className="relative w-full">
          <article className="mx-auto w-full rounded-[1.75rem] border border-white/80 bg-white p-6 shadow-[0_22px_60px_rgba(8,11,26,0.2)] sm:p-8 lg:mx-0 lg:max-w-[40rem] lg:p-10 xl:max-w-[50rem] 2xl:max-w-[54rem]">
            <HeroCopy props={props} />
          </article>
        </div>
      </div>
    </div>
  );
};

HeroBackgroundImage.displayName = "content::HeroBackgroundImage";
